// Updated: 2026-08-03 21:45:00 CDT
// Purpose: Keep public voice assignments aligned with the characters that actually produce audio.

import type { Character } from "@/types/screenplay";

export interface VoiceAssignmentReadiness {
  requiredCharacters: string[];
  missingCharacters: string[];
  unavailableCharacters: string[];
  isComplete: boolean;
}

const uniqueNames = (names: string[]) =>
  names.filter((name, index) => name && names.indexOf(name) === index);

export const requiredVoiceCharacters = (characters: Character[]): string[] => {
  const sequence = characters.find(
    (character) => character.name === "SCREENPLAY_SEQUENCE",
  )?.sequence;

  if (sequence?.length) {
    return uniqueNames(
      sequence.map((item) =>
        item.type === "dialogue" && item.character
          ? item.character
          : "SCENE NARRATOR",
      ),
    );
  }

  return uniqueNames(
    characters
      .filter((character) => character.name !== "SCREENPLAY_SEQUENCE")
      .map((character) => character.name),
  );
};

export const assessVoiceAssignments = (
  characters: Character[],
  assignments: Record<string, string>,
  availableVoiceIds?: ReadonlySet<string>,
): VoiceAssignmentReadiness => {
  const requiredCharacters = requiredVoiceCharacters(characters);
  const missingCharacters = requiredCharacters.filter(
    (character) => !assignments[character]?.trim(),
  );
  const unavailableCharacters = availableVoiceIds
    ? requiredCharacters.filter((character) => {
        const voiceId = assignments[character]?.trim();
        return Boolean(voiceId && !availableVoiceIds.has(voiceId));
      })
    : [];

  return {
    requiredCharacters,
    missingCharacters,
    unavailableCharacters,
    isComplete:
      requiredCharacters.length > 0 &&
      missingCharacters.length === 0 &&
      unavailableCharacters.length === 0,
  };
};

/** Shared actors are allowed; report only roles that are part of this screenplay. */
export const duplicateVoiceAssignments = (
  characters: Character[],
  assignments: Record<string, string>,
): { voiceId: string; roles: string[] }[] => {
  const rolesByVoice = new Map<string, string[]>();
  for (const role of requiredVoiceCharacters(characters)) {
    const voiceId = assignments[role]?.trim();
    if (!voiceId) continue;
    const roles = rolesByVoice.get(voiceId) ?? [];
    roles.push(role);
    rolesByVoice.set(voiceId, roles);
  }
  return [...rolesByVoice].filter(([, roles]) => roles.length > 1)
    .map(([voiceId, roles]) => ({ voiceId, roles }));
};

export const sanitizeVoiceAssignments = (
  characters: Character[],
  assignments: Record<string, string>,
  availableVoiceIds: ReadonlySet<string>,
  options: { preserveUnavailable?: boolean } = {},
): Record<string, string> => {
  const requiredCharacters = new Set(requiredVoiceCharacters(characters));

  return Object.fromEntries(
    Object.entries(assignments).filter(
      ([character, voiceId]) =>
        requiredCharacters.has(character) && (options.preserveUnavailable || availableVoiceIds.has(voiceId)),
    ),
  );
};
